import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Shield, FileText, Heart, Activity, Users, Home, CheckCircle, ChevronDown, ChevronUp, ExternalLink, HelpCircle } from 'lucide-react';

export const FamilyPhysicianResources: React.FC = () => {
  const [activeAccordion, setActiveAccordion] = useState<number | null>(0);

  const toggleAccordion = (index: number) => {
    setActiveAccordion(activeAccordion === index ? null : index);
  };

  const faqs = [
    {
      question: "What insurance plans do you accept?",
      answer: "We accept most major insurance plans including Blue Cross Blue Shield, Aetna, Cigna, UnitedHealthcare, Medicare, Medicaid, Tricare, and many others. Please contact our office to verify that we accept your specific insurance plan."
    },
    {
      question: "How do I schedule an appointment?",
      answer: "You can schedule an appointment by using our online booking system, calling our office during business hours at (555) 123-4567, or sending a request through the patient portal if you're an existing patient."
    },
    {
      question: "What should I bring to my first appointment?",
      answer: "Please bring your insurance card, photo ID, list of current medications (including dosages), medical records if available, and completed new patient forms which can be downloaded from our website or patient portal. Arriving 15 minutes early helps us process your information efficiently."
    },
    {
      question: "Do you offer telehealth appointments?",
      answer: "Yes, we offer telehealth appointments for certain types of visits including follow-ups, medication management, and minor illness consultations. Please call our office or check the patient portal to determine if your concern can be addressed through a virtual visit."
    },
    {
      question: "How do I refill my prescription?",
      answer: "The easiest way to request a prescription refill is through our patient portal. Alternatively, you can contact your pharmacy and they will send us a refill request, or you can call our office directly. Please allow 48 hours for prescription refills to be processed."
    }
  ];

  const resources = [
    {
      title: "Preventive Health Guidelines",
      description: "Age-specific recommendations for screenings, immunizations, and health maintenance.",
      icon: <Shield className="h-8 w-8 text-primary-600" />,
      link: "#preventive-health"
    },
    {
      title: "Medication Information",
      description: "Resources for understanding your medications, potential side effects, and proper usage.",
      icon: <FileText className="h-8 w-8 text-primary-600" />,
      link: "#medication-info"
    },
    {
      title: "Healthy Living Tips",
      description: "Guidance on nutrition, exercise, stress management, and other lifestyle factors.",
      icon: <Heart className="h-8 w-8 text-primary-600" />,
      link: "#healthy-living"
    },
    {
      title: "Common Illness Care",
      description: "Information on managing common conditions like colds, flu, allergies, and minor injuries.",
      icon: <Activity className="h-8 w-8 text-primary-600" />,
      link: "#common-illness"
    },
    {
      title: "Mental Health Resources",
      description: "Support resources for anxiety, depression, stress, and other mental health concerns.",
      icon: <Users className="h-8 w-8 text-primary-600" />,
      link: "#mental-health"
    },
    {
      title: "Community Resources",
      description: "Local resources for additional support services, specialists, and community health programs.",
      icon: <Home className="h-8 w-8 text-primary-600" />,
      link: "#community"
    }
  ];

  const forms = [
    {
      title: "New Patient Registration",
      description: "Required for all new patients prior to first appointment",
      format: "PDF",
      link: "#new-patient-form"
    },
    {
      title: "Medical History Form",
      description: "Comprehensive health history questionnaire",
      format: "PDF",
      link: "#medical-history"
    },
    {
      title: "HIPAA Privacy Notice",
      description: "Information about how we protect your medical information",
      format: "PDF",
      link: "#hipaa-notice"
    },
    {
      title: "Release of Medical Records",
      description: "Authorization to release your records to another provider",
      format: "PDF",
      link: "#records-release"
    },
    {
      title: "Insurance Information Form",
      description: "Details about your current insurance coverage",
      format: "PDF",
      link: "#insurance-form"
    },
    {
      title: "Medication Refill Request",
      description: "Form to request prescription refills",
      format: "PDF",
      link: "#medication-refill"
    }
  ];

  const insurances = [
    "Blue Cross Blue Shield", "Aetna", "Cigna", "UnitedHealthcare", 
    "Medicare", "Medicaid", "Humana", "Kaiser Permanente", 
    "Anthem", "Tricare", "Health Alliance Plan", "Priority Health"
  ];

  return (
    <div>
      {/* Hero Section */}
      <section className="pt-32 pb-20 bg-gradient-to-r from-primary-600 to-accent-500 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h1 className="text-4xl sm:text-5xl font-bold mb-6">Patient Resources</h1>
            <p className="text-xl text-white/90 max-w-3xl mx-auto leading-relaxed">
              Helpful information, forms, and resources to help you manage your healthcare.
            </p>
          </div>
        </div>
      </section>

      {/* Patient Portal */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl font-bold text-gray-900 mb-6">Patient Portal</h2>
              <p className="text-gray-600 mb-6 leading-relaxed">
                Our secure patient portal gives you 24/7 access to your health information and convenient 
                online services. It's a simple, secure way to manage your healthcare from anywhere.
              </p>
              
              <h3 className="text-xl font-semibold text-gray-900 mb-4">With the Patient Portal, you can:</h3>
              <ul className="space-y-3 mb-6">
                <li className="flex items-center text-gray-600">
                  <CheckCircle className="h-5 w-5 text-primary-600 mr-3 flex-shrink-0" />
                  Request appointments and view upcoming visits
                </li>
                <li className="flex items-center text-gray-600">
                  <CheckCircle className="h-5 w-5 text-primary-600 mr-3 flex-shrink-0" />
                  Message your healthcare team securely
                </li>
                <li className="flex items-center text-gray-600">
                  <CheckCircle className="h-5 w-5 text-primary-600 mr-3 flex-shrink-0" />
                  View test results and health records
                </li>
                <li className="flex items-center text-gray-600">
                  <CheckCircle className="h-5 w-5 text-primary-600 mr-3 flex-shrink-0" />
                  Request prescription refills
                </li>
                <li className="flex items-center text-gray-600">
                  <CheckCircle className="h-5 w-5 text-primary-600 mr-3 flex-shrink-0" />
                  Update your contact and insurance information
                </li>
                <li className="flex items-center text-gray-600">
                  <CheckCircle className="h-5 w-5 text-primary-600 mr-3 flex-shrink-0" />
                  Pay bills online
                </li>
              </ul>
              
              <div className="flex flex-col sm:flex-row gap-4">
                <a href="#patient-portal" className="bg-primary-600 text-white px-6 py-3 rounded-lg font-medium hover:bg-primary-700 transition-colors text-center">
                  Access Patient Portal
                </a>
                <Link to="/templates/family-physician#contact" className="bg-gray-100 text-gray-800 px-6 py-3 rounded-lg font-medium hover:bg-gray-200 transition-colors text-center">
                  Need Help? Contact Us
                </Link>
              </div>
            </div>
            
            <div>
              <img 
                src="https://images.pexels.com/photos/5407038/pexels-photo-5407038.jpeg?auto=compress&cs=tinysrgb&w=800&h=600&fit=crop" 
                alt="Patient using laptop" 
                className="rounded-lg shadow-lg"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Forms & Documents */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Forms & Documents</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Download and complete these forms before your visit to save time at the office.
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {forms.map((form, index) => (
              <div key={index} className="bg-white p-6 rounded-lg shadow-md">
                <div className="flex items-start">
                  <div className="bg-primary-100 p-3 rounded-lg mr-4">
                    <FileText className="h-6 w-6 text-primary-600" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-1">{form.title}</h3>
                    <p className="text-gray-600 text-sm mb-3">{form.description}</p>
                    <div className="flex items-center justify-between">
                      <span className="text-xs text-gray-500">{form.format}</span>
                      <a 
                        href={form.link} 
                        className="text-primary-600 hover:text-primary-700 font-medium text-sm flex items-center"
                      >
                        Download
                        <ExternalLink className="ml-1 h-4 w-4" />
                      </a>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQs */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Frequently Asked Questions</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Find answers to common questions about our practice and services.
            </p>
          </div>
          
          <div className="max-w-3xl mx-auto">
            <div className="space-y-6">
              {faqs.map((faq, index) => (
                <div key={index} className="bg-gray-50 rounded-lg shadow-md overflow-hidden">
                  <button
                    className="flex justify-between items-center w-full p-6 text-left focus:outline-none"
                    onClick={() => toggleAccordion(index)}
                  >
                    <h3 className="text-lg font-semibold text-gray-900">{faq.question}</h3>
                    {activeAccordion === index ? (
                      <ChevronUp className="h-5 w-5 text-primary-600" />
                    ) : (
                      <ChevronDown className="h-5 w-5 text-primary-600" />
                    )}
                  </button>
                  
                  {activeAccordion === index && (
                    <div className="px-6 pb-6">
                      <p className="text-gray-600 leading-relaxed">{faq.answer}</p>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
          
          <div className="mt-12 text-center">
            <p className="text-gray-600 mb-4">
              Have a question that's not answered here? We're happy to help.
            </p>
            <Link to="/templates/family-physician#contact" className="bg-primary-600 text-white px-6 py-3 rounded-lg font-medium hover:bg-primary-700 transition-colors inline-block">
              Contact Us
            </Link>
          </div>
        </div>
      </section>

      {/* Insurance Information */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Insurance Information</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              We accept most major insurance plans to make quality healthcare accessible to our community.
            </p>
          </div>
          
          <div className="bg-white p-8 rounded-lg shadow-md max-w-3xl mx-auto">
            <h3 className="text-xl font-semibold text-gray-900 mb-6">Accepted Insurance Plans:</h3>
            
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-8">
              {insurances.map((insurance, index) => (
                <div key={index} className="flex items-center">
                  <CheckCircle className="h-5 w-5 text-primary-600 mr-2 flex-shrink-0" />
                  <span className="text-gray-600">{insurance}</span>
                </div>
              ))}
            </div>
            
            <p className="text-gray-600 mb-6">
              Please contact our office to verify that we accept your specific insurance plan. Our staff will 
              work with you to verify your coverage and maximize your benefits.
            </p>
            
            <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4">
              <div className="flex">
                <div className="flex-shrink-0">
                  <HelpCircle className="h-5 w-5 text-yellow-400" />
                </div>
                <div className="ml-3">
                  <p className="text-sm text-yellow-700">
                    Please bring your insurance card and photo ID to every appointment. If your insurance 
                    changes, please notify us as soon as possible.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Health Resources */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Health Resources</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Educational resources to help you make informed decisions about your health.
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {resources.map((resource, index) => (
              <div key={index} className="bg-gray-50 p-6 rounded-lg shadow-md">
                <div className="mb-4">
                  {resource.icon}
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-2">{resource.title}</h3>
                <p className="text-gray-600 mb-4">{resource.description}</p>
                <a 
                  href={resource.link} 
                  className="text-primary-600 font-medium hover:text-primary-700 transition-colors flex items-center"
                >
                  Learn More
                  <ExternalLink className="ml-1 h-4 w-4" />
                </a>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-primary-600 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold mb-6">Have Questions?</h2>
          <p className="text-xl text-primary-100 mb-8">
            Our team is here to help. Contact us with any questions about our services or resources.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/templates/family-physician#contact" className="bg-white text-primary-600 px-6 py-3 rounded-lg font-medium hover:bg-gray-100 transition-colors">
              Contact Us
            </Link>
            <Link to="/templates/family-physician#appointment" className="bg-transparent border-2 border-white text-white px-6 py-3 rounded-lg font-medium hover:bg-white/10 transition-colors">
              Book an Appointment
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};